// Comparison: click a dot → its details (?live=1, Ben 2026-10-19 — Catalyst's detail panel,
// made better): who it is (club · league, position, age, minutes), then each stat on the chart with
// its value and its percentile among the players plotted. Clicking only opens this; the player's name
// drags out like the dots. ✕, the same dot again, or an empty part of the chart closes it.

import type { PointerEvent as ReactPointerEvent } from "react";
import type { ComparisonPoint } from "../data/comparison.js";
import { getPlayerShortName } from "../data/player-utils.js";
import { playerIdOf } from "../data/players.js";
import { formatMetricValue } from "./comparison-view.js";
import { humanizePositionTemplate, isLowerBetter } from "./radar-view.js";
import { percentBetter } from "./stats-view.js";
import { startPlayerTokenDragOnMove } from "../canvas/menu-drag.js";
import { setStickyPlayer } from "../canvas/sticky-player.js";

interface Stat {
  role: string;
  key: string;
  name: string;
  outputType?: string;
  value: number | null;
}

/** "91st". */
function ordinal(n: number): string {
  const t = n % 100;
  const last = n % 10;
  return `${n}${t >= 11 && t <= 13 ? "th" : last === 1 ? "st" : last === 2 ? "nd" : last === 3 ? "rd" : "th"}`;
}

function ageOf(dob: string | null | undefined): number | null {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  if (now.getMonth() < d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())) age--;
  return age;
}

export function ComparisonDetails({
  point,
  points,
  stats,
  onClose,
  onDrag,
}: {
  point: ComparisonPoint;
  /** Everyone plotted: the percentiles are out of them. */
  points: readonly ComparisonPoint[];
  stats: readonly Omit<Stat, "value">[];
  onClose: () => void;
  onDrag: (player: ComparisonPoint["player"]) => void;
}) {
  const pl = point.player;
  const valueOf = (role: string) => (role === "x" ? point.x : role === "y" ? point.y : role === "size" ? point.size : point.color);
  const groupOf = (role: string) =>
    points
      .map((p) => (role === "x" ? p.x : role === "y" ? p.y : role === "size" ? p.size : p.color))
      .filter((v): v is number => v !== null && !Number.isNaN(v))
      .sort((a, b) => a - b);
  const age = ageOf(pl.dob);
  const minutes = (pl.stats as Record<string, number | null> | undefined)?.minutes ?? null;
  const facts = [
    pl.positionTemplate ? humanizePositionTemplate(pl.positionTemplate) : null,
    age !== null ? `${age} years old` : null,
    minutes !== null ? `${Math.round(minutes).toLocaleString()} minutes` : null,
  ].filter(Boolean);

  const onPress = (e: ReactPointerEvent<HTMLElement>) => {
    if (window.innerWidth <= 768) return;
    e.stopPropagation();
    startPlayerTokenDragOnMove(e.nativeEvent, () => {
      onDrag(pl);
      setStickyPlayer(playerIdOf(pl));
    });
  };

  return (
    <div className="cmp-details" onPointerDown={(e) => e.stopPropagation()}>
      <button type="button" className="cmp-details-close" title="Close" onClick={onClose}>
        ✕
      </button>
      <div className="cmp-details-name" data-tooltip="Drag onto the canvas or a module" onPointerDown={onPress}>
        {getPlayerShortName(pl)}
      </div>
      <div className="cmp-details-meta">{[pl.team, pl.competition].filter(Boolean).join(" · ")}</div>
      {facts.length > 0 && <div className="cmp-details-meta">{facts.join(" · ")}</div>}
      <div className="cmp-details-stats">
        {stats.map((s) => {
          const v = valueOf(s.role);
          const pct = v === null ? null : percentBetter(v, groupOf(s.role), isLowerBetter(s.key));
          return (
            <div className="cmp-details-row" key={s.role}>
              <span className="cmp-details-stat">{s.name}</span>
              <span className="cmp-details-value">{formatMetricValue(v, s.outputType)}</span>
              <span className="cmp-details-pct">{pct === null ? "" : `${ordinal(pct)} pct`}</span>
            </div>
          );
        })}
      </div>
      <div className="cmp-details-foot">Percentiles out of the {points.length.toLocaleString()} dots on the chart.</div>
    </div>
  );
}
